import gql from 'graphql-tag';
import { useQuery, provideApolloClient } from '@vue/apollo-composable';
import { ref, watch } from 'vue';
import apolloClient from './useApolloClient';

const GET_LEAD_HISTORY = gql`
  query LeadHistoryQuery($leadId: ID!) {
    leadHistory(id: $leadId) {
      LEADID
      JOBNAME
      NOTES
      SOURCE
      CREATEDATE
      AMOUNT
    }
  }
`;

// Global state management for lead history
const historyCache = ref(new Map());
const loadingStates = ref(new Map());
const errorStates = ref(new Map());

export default function useLeadHistoryQuery() {
  provideApolloClient(apolloClient);

  const fetchLeadHistory = (leadId) => {
    if (!leadId) return;

    // Initialize states if not present
    if (!historyCache.value.has(leadId)) {
      historyCache.value.set(leadId, []);
    }
    if (!loadingStates.value.has(leadId)) {
      loadingStates.value.set(leadId, false);
    }
    if (!errorStates.value.has(leadId)) {
      errorStates.value.set(leadId, false);
    }

    // Set loading state
    loadingStates.value.set(leadId, true);
    errorStates.value.set(leadId, false);

    const { result, error, loading, refetch } = useQuery(GET_LEAD_HISTORY, { leadId }, { fetchPolicy: 'no-cache' });

    // Watch for results
    watch(result, (data) => {
      if (data?.leadHistory) {
        historyCache.value.set(leadId, data.leadHistory);
      }
      loadingStates.value.set(leadId, false);
    });

    // Watch for errors
    watch(error, (err) => {
      if (err) {
        console.error('Error fetching lead history:', err);
        errorStates.value.set(leadId, true);
        loadingStates.value.set(leadId, false);
      }
    });

    return { result, error, loading, refetch };
  };

  const getLeadHistory = (leadId) => {
    return {
      data: historyCache.value.get(leadId) || [],
      loading: loadingStates.value.get(leadId) || false,
      error: errorStates.value.get(leadId) || false,
    };
  };

  const refetchLeadHistory = (leadId) => {
    if (leadId) {
      fetchLeadHistory(leadId);
    }
  };

  return {
    fetchLeadHistory,
    getLeadHistory,
    refetchLeadHistory,
    historyCache,
    loadingStates,
    errorStates,
  };
}
